import {
  aiLanguagePracticePages,
  getAiTutorPath,
  getSpeakingPracticePath,
} from "./aiLanguagePracticePages";
import { getFillInTheGapLanguageBySlug } from "./fillInTheGapLanguagePages";
import type { SeoFaq } from "./jsonLd";
import { getLanguageTargetPage } from "./languageTargetPages";

export type DuolingoAlternativeSupportLink = {
  label: string;
  href: string;
  description: string;
};

export type DuolingoAlternativeSource = {
  label: string;
  href: string;
};

export type DuolingoAlternativePage = {
  slug: string;
  languageName: string;
  languageCode: string;
  title: string;
  description: string;
  updatedAt: string;
  queryFocus: string[];
  searchIntent: string;
  duolingoFit: string;
  speakingGap: string;
  chickyTutorFit: string;
  learnerBlockers: string[];
  practicePlan: string[];
  starterPrompts: string[];
  correctionExamples: string[];
  decisionRules: string[];
  faq: SeoFaq[];
  supportLinks: DuolingoAlternativeSupportLink[];
  sources: DuolingoAlternativeSource[];
};

type RawDuolingoAlternativePage = Omit<
  DuolingoAlternativePage,
  "supportLinks" | "sources" | "title" | "description" | "updatedAt" | "languageCode"
> & {
  learnSlug: string;
  aiTutorSlug?: string;
  toolSlug?: string;
  blogSlug?: string;
};

const updatedAt = "2026-05-21";

const rawPages: RawDuolingoAlternativePage[] = [
  {
    slug: "japanese",
    languageName: "Japanese",
    learnSlug: "japanese",
    aiTutorSlug: "japanese",
    toolSlug: "japanese",
    blogSlug: "best-japanese-learning-apps-and-7-alternatives-when-duolingo-isnt-enough",
    queryFocus: [
      "duolingo alternatives japanese",
      "best duolingo alternatives for learning japanese 2026",
      "alternative to duolingo for japanese",
    ],
    searchIntent:
      "The learner is already aware of Duolingo, but wants a Japanese alternative that helps with output, particles, politeness, and real speech.",
    duolingoFit:
      "Duolingo can be useful for habit, recognition, and beginner exposure before a learner is ready for open-ended speaking.",
    speakingGap:
      "Japanese learners often know grammar points from lessons but still hesitate when they need to choose particles, keep the right politeness level, or answer quickly.",
    chickyTutorFit:
      "ChickyTutor is a better fit when the missing practice is live Japanese conversation with correction after the learner speaks.",
    learnerBlockers: [
      "Turning English-shaped thoughts into shorter Japanese replies.",
      "Choosing particles under pressure instead of from a worksheet.",
      "Keeping polite speech consistent across a multi-turn conversation.",
    ],
    practicePlan: [
      "Start with a polite self-introduction and ask for particle corrections only.",
      "Roleplay restaurant orders with short replies before expanding the answer.",
      "Repeat one corrected sentence aloud before moving to the next turn.",
    ],
    starterPrompts: [
      "Practice a polite Japanese self-introduction and correct only my particles.",
      "Roleplay ordering ramen in Japanese. Keep each reply short and natural.",
      "Ask me three hobby questions in Japanese, then make one answer more natural.",
    ],
    correctionExamples: [
      "Repair particle choice in short sentences before practicing longer replies.",
      "Switch one answer from too-casual speech into polite speech.",
      "Repeat long vowels and pitch-sensitive phrases after a corrected model.",
    ],
    decisionRules: [
      "Use Duolingo for daily exposure if you are still building the basic habit.",
      "Use ChickyTutor when you need to answer out loud and repair mistakes in conversation.",
      "Use both if Duolingo keeps you consistent and ChickyTutor turns the lesson into speech.",
    ],
    faq: [
      {
        question: "What is a good Duolingo alternative for Japanese speaking practice?",
        answer:
          "Use a voice-first tutor when your main blocker is speaking. ChickyTutor lets you practice Japanese replies, get corrections, and repeat repaired sentences aloud.",
      },
      {
        question: "Why do Japanese learners need more than app lessons?",
        answer:
          "Japanese speaking requires fast choices around word order, particles, politeness, and natural short replies. Those skills need live output, not only recognition exercises.",
      },
      {
        question: "Can I use ChickyTutor with Duolingo for Japanese?",
        answer:
          "Yes. A practical workflow is to study the lesson topic first, then use ChickyTutor to roleplay that topic and correct one spoken mistake at a time.",
      },
    ],
  },
  {
    slug: "spanish",
    languageName: "Spanish",
    learnSlug: "latin-american-spanish",
    aiTutorSlug: "spanish",
    toolSlug: "spanish-spain",
    blogSlug: "best-spanish-learning-apps-and-7-alternatives-when-duolingo-isnt-enough",
    queryFocus: [
      "alternatives to duolingo for spanish",
      "best duolingo alternatives for learning spanish 2026",
      "free alternatives to duolingo for spanish",
    ],
    searchIntent:
      "The learner wants a Spanish alternative that moves beyond streaks and into confident spoken replies.",
    duolingoFit:
      "Duolingo can help Spanish beginners build a daily habit, review vocabulary, and keep exposure lightweight.",
    speakingGap:
      "Spanish learners can often read or tap through exercises but still pause when they need to choose tense, roll an r, or answer a follow-up question.",
    chickyTutorFit:
      "ChickyTutor is stronger when the goal is Spanish speaking confidence, correction loops, and roleplay practice.",
    learnerBlockers: [
      "Translating every answer from English before speaking.",
      "Knowing verb forms but choosing the wrong tense in conversation.",
      "Using written textbook sentences instead of short natural replies.",
    ],
    practicePlan: [
      "Start with a five-minute food-ordering roleplay.",
      "Ask the tutor to correct only one verb-tense issue per exchange.",
      "Repeat the corrected reply twice: once slowly, once at conversation speed.",
    ],
    starterPrompts: [
      "Roleplay ordering lunch in Spanish and correct one unnatural phrase.",
      "Ask me follow-up questions about my weekend in Spanish.",
      "Help me say the same Spanish answer in a casual and a more polite way.",
    ],
    correctionExamples: [
      "Repair ser versus estar in a sentence about mood or location.",
      "Shorten a written-sounding answer into a natural spoken reply.",
      "Practice tapped r sounds inside real travel phrases.",
    ],
    decisionRules: [
      "Use Duolingo if you need a low-friction Spanish study habit.",
      "Use ChickyTutor if the bottleneck is spoken output and confidence.",
      "Use ChickyTutor after Duolingo lessons to turn vocabulary into conversation.",
    ],
    faq: [
      {
        question: "What is the best Duolingo alternative for Spanish speaking?",
        answer:
          "For speaking practice, choose a tool that makes you answer out loud. ChickyTutor focuses on Spanish conversation, corrections, and repeatable roleplays.",
      },
      {
        question: "Is ChickyTutor better than Duolingo for Spanish?",
        answer:
          "It depends on the job. Duolingo is useful for habit and basics. ChickyTutor is better when you need live Spanish speaking practice and feedback.",
      },
      {
        question: "Can beginners use ChickyTutor for Spanish?",
        answer:
          "Yes. Beginners can keep sessions short, ask for English explanations, and practice simple Spanish replies before longer conversation.",
      },
    ],
  },
  {
    slug: "thai",
    languageName: "Thai",
    learnSlug: "thai",
    toolSlug: "thai",
    blogSlug: "best-thai-learning-apps-and-7-alternatives-when-duolingo-isnt-enough",
    queryFocus: [
      "thai duolingo alternative",
      "why isn't thai on duolingo",
      "duolingo thai available 2026",
    ],
    searchIntent:
      "The learner is looking for a Thai path after finding that familiar app options may not match their course or speaking needs.",
    duolingoFit:
      "Duolingo is best treated as one possible habit tool; learners should check Duolingo's current course list directly before relying on it for Thai.",
    speakingGap:
      "Thai learners need careful tone, vowel length, particles, and polite endings, which are hard to build from passive study alone.",
    chickyTutorFit:
      "ChickyTutor can give Thai learners a low-pressure place to speak, repeat corrected phrases, and practice practical scenarios.",
    learnerBlockers: [
      "Avoiding speech because tones feel exposed.",
      "Reading romanized prompts without building Thai sound habits.",
      "Forgetting polite particles when a reply becomes longer.",
    ],
    practicePlan: [
      "Practice greetings and polite particles before longer conversation.",
      "Use short food and taxi roleplays with tone correction.",
      "Repeat corrected Thai phrases slowly, then at a natural pace.",
    ],
    starterPrompts: [
      "Practice Thai greetings and correct my tone gently.",
      "Roleplay ordering street food in Thai with short replies.",
      "Teach me Thai repair phrases for asking someone to repeat or slow down.",
    ],
    correctionExamples: [
      "Repair tone drift on one high-frequency phrase.",
      "Add the right polite particle to a short request.",
      "Shorten an English-shaped sentence into a usable Thai reply.",
    ],
    decisionRules: [
      "Check whether your preferred app currently supports the exact Thai course you want.",
      "Use ChickyTutor when your main need is spoken Thai practice with correction.",
      "Pair ChickyTutor with a textbook, teacher, or course if you also want a formal curriculum.",
    ],
    faq: [
      {
        question: "Is ChickyTutor a Thai Duolingo alternative?",
        answer:
          "It can be a strong alternative for Thai speaking practice because the session is based on voice replies, corrections, and repeatable roleplays.",
      },
      {
        question: "Does this page claim Duolingo does not support Thai?",
        answer:
          "No. Course availability can change, so learners should check Duolingo directly. This page focuses on what to use when the goal is Thai speaking practice.",
      },
      {
        question: "What should Thai beginners practice first?",
        answer:
          "Start with greetings, polite particles, food ordering, taxi phrases, and repair phrases. Keep replies short until tones and rhythm feel more stable.",
      },
    ],
  },
  {
    slug: "lithuanian",
    languageName: "Lithuanian",
    learnSlug: "lithuanian",
    toolSlug: "lithuanian",
    blogSlug: "best-lithuanian-learning-apps-and-7-alternatives-when-duolingo-isnt-enough",
    queryFocus: ["is lithuanian on duolingo", "duolingo lithuanian", "lithuanian duolingo"],
    searchIntent:
      "The learner is checking Duolingo for Lithuanian and needs a practical next step if the available app path is not enough.",
    duolingoFit:
      "Duolingo can be useful when it offers the exact course a learner wants, but Lithuanian learners often need more direct speaking practice and feedback.",
    speakingGap:
      "Lithuanian speech can feel intimidating because learners need to coordinate cases, endings, stress, and natural short replies.",
    chickyTutorFit:
      "ChickyTutor gives Lithuanian learners a place to practice short spoken exchanges and ask for corrections in English.",
    learnerBlockers: [
      "Overthinking case endings before saying anything.",
      "Avoiding spoken Lithuanian because stress and endings feel uncertain.",
      "Practicing vocabulary lists without using the words in replies.",
    ],
    practicePlan: [
      "Start with introductions and family or city questions.",
      "Ask for one case-ending correction per answer.",
      "Repeat corrected Lithuanian phrases until the ending feels automatic.",
    ],
    starterPrompts: [
      "Help me introduce myself in Lithuanian and correct one case ending.",
      "Ask me simple Lithuanian questions about where I live.",
      "Roleplay buying something in Lithuanian with short beginner replies.",
    ],
    correctionExamples: [
      "Repair one noun ending inside a short answer.",
      "Make a vocabulary-list sentence sound like a spoken reply.",
      "Practice stress on a corrected everyday phrase.",
    ],
    decisionRules: [
      "Use Duolingo if it supports your exact Lithuanian study path and habit goal.",
      "Use ChickyTutor when you need Lithuanian speaking reps and correction.",
      "Use additional structured resources when you want a complete grammar sequence.",
    ],
    faq: [
      {
        question: "What should I use if I am looking for Lithuanian on Duolingo?",
        answer:
          "Check Duolingo directly for current course availability. If your goal is speaking Lithuanian, ChickyTutor can help with voice practice and corrections.",
      },
      {
        question: "Is Lithuanian hard to practice with an AI tutor?",
        answer:
          "It requires careful correction, but short speaking loops work well: introductions, location questions, shopping phrases, and repeated corrected endings.",
      },
      {
        question: "Can ChickyTutor explain Lithuanian mistakes in English?",
        answer:
          "Yes. Use English as the support language while keeping the spoken practice focused on Lithuanian.",
      },
    ],
  },
  {
    slug: "french",
    languageName: "French",
    learnSlug: "french",
    aiTutorSlug: "french",
    toolSlug: "french",
    blogSlug: "best-french-learning-apps-and-7-alternatives-when-duolingo-isnt-enough",
    queryFocus: [
      "alternatives to duolingo for french",
      "duolingo alternatives french",
      "best duolingo alternatives for learning french 2026",
    ],
    searchIntent:
      "The learner wants a French alternative that improves pronunciation and conversation, not only vocabulary recognition.",
    duolingoFit: "Duolingo can be useful for French habit, vocabulary, and beginner exposure.",
    speakingGap:
      "French learners often read better than they speak because liaison, silent endings, gender agreement, and rhythm need spoken repetition.",
    chickyTutorFit:
      "ChickyTutor is strongest when the learner wants French roleplays, pronunciation-aware correction, and repeated repaired phrases.",
    learnerBlockers: [
      "Pronouncing silent endings from the written word.",
      "Avoiding speech because nasal vowels and liaison feel embarrassing.",
      "Using English word order inside French replies.",
    ],
    practicePlan: [
      "Practice a cafe order and one follow-up question.",
      "Ask for liaison and silent-ending correction in the same phrase.",
      "Repeat a corrected answer with natural rhythm before continuing.",
    ],
    starterPrompts: [
      "Roleplay ordering coffee in French and correct my liaison.",
      "Ask me simple French travel questions and fix one gender agreement mistake.",
      "Help me make this French sentence sound less translated from English.",
    ],
    correctionExamples: [
      "Remove a silent ending that should not be pronounced.",
      "Repair gender agreement in a short opinion.",
      "Practice u versus ou inside a live answer.",
    ],
    decisionRules: [
      "Use Duolingo if you need French habit and beginner vocabulary.",
      "Use ChickyTutor when pronunciation and live conversation are the blocker.",
      "Use both if you want lessons for consistency and voice practice for confidence.",
    ],
    faq: [
      {
        question: "What is a good Duolingo alternative for French speaking?",
        answer:
          "ChickyTutor is a strong fit when you want live French speaking practice, pronunciation correction, and short roleplays.",
      },
      {
        question: "Is ChickyTutor better for French pronunciation?",
        answer:
          "It is built around speaking sessions, so it can focus the practice on liaison, silent endings, nasal vowels, and corrected repetition.",
      },
      {
        question: "Can I use ChickyTutor after Duolingo French?",
        answer:
          "Yes. Use ChickyTutor to practice the conversations that still feel hard after app lessons.",
      },
    ],
  },
  {
    slug: "croatian",
    languageName: "Croatian",
    learnSlug: "croatian",
    toolSlug: "croatian",
    blogSlug: "best-croatian-learning-apps-and-7-alternatives-when-duolingo-isnt-enough",
    queryFocus: ["duolingo croatian", "croatian duolingo", "duolingo croatian available 2026"],
    searchIntent:
      "The learner is checking whether a familiar app can help with Croatian and needs a practical speaking-first path.",
    duolingoFit:
      "Duolingo can be a useful habit tool when it has the exact language path a learner wants, but availability and course depth should be checked directly.",
    speakingGap:
      "Croatian learners need practice with cases, endings, word stress, and natural everyday exchanges, not only recognition.",
    chickyTutorFit:
      "ChickyTutor can help Croatian learners practice short voice sessions with English explanations and targeted corrections.",
    learnerBlockers: [
      "Pausing before every noun because case endings feel risky.",
      "Reading Croatian more easily than producing short replies.",
      "Avoiding travel or family conversations because pronunciation feels uncertain.",
    ],
    practicePlan: [
      "Start with greetings, names, and where-you-are-from questions.",
      "Ask for one case or ending correction per reply.",
      "Roleplay cafe, directions, and family topics in short loops.",
    ],
    starterPrompts: [
      "Help me introduce myself in Croatian and correct one ending.",
      "Roleplay ordering coffee in Croatian with beginner-friendly replies.",
      "Ask me Croatian travel questions and keep the corrections short.",
    ],
    correctionExamples: [
      "Repair one case ending without stopping the whole conversation.",
      "Make a travel phrase sound more natural and less translated.",
      "Repeat a corrected Croatian phrase for stress and clarity.",
    ],
    decisionRules: [
      "Check Duolingo directly if your question is current Croatian course availability.",
      "Use ChickyTutor when you need Croatian speaking practice even without a traditional app path.",
      "Add a structured grammar source if you want a full case-by-case curriculum.",
    ],
    faq: [
      {
        question: "Is ChickyTutor a Croatian Duolingo alternative?",
        answer:
          "It is a good alternative for the speaking-practice job: short Croatian conversations, corrections, and repeatable beginner roleplays.",
      },
      {
        question: "Does this page answer whether Croatian is currently on Duolingo?",
        answer:
          "No. Duolingo course availability can change, so check Duolingo directly. This page focuses on Croatian speaking practice alternatives.",
      },
      {
        question: "What should Croatian beginners practice first?",
        answer:
          "Start with introductions, cafe orders, directions, and family questions. Ask for one ending correction at a time.",
      },
    ],
  },
];

function getAiPracticePage(slug?: string) {
  if (!slug) return undefined;
  return aiLanguagePracticePages.find((page) => page.slug === slug);
}

function buildSupportLinks(page: RawDuolingoAlternativePage): DuolingoAlternativeSupportLink[] {
  const links: DuolingoAlternativeSupportLink[] = [];
  const learnPage = getLanguageTargetPage(page.learnSlug);
  const aiPracticePage = getAiPracticePage(page.aiTutorSlug);
  const toolPage = getFillInTheGapLanguageBySlug(page.toolSlug ?? page.learnSlug);

  if (learnPage) {
    links.push({
      label: `Learn ${learnPage.targetName}`,
      href: learnPage.path,
      description: `Open ChickyTutor with ${learnPage.targetName} selected for speaking-first practice.`,
    });
  }

  if (aiPracticePage) {
    links.push({
      label: `AI ${aiPracticePage.name} tutor`,
      href: getAiTutorPath(aiPracticePage),
      description: `${aiPracticePage.name} tutor sessions with correction loops and prompts.`,
    });
    links.push({
      label: `${aiPracticePage.name} speaking practice`,
      href: getSpeakingPracticePath(aiPracticePage),
      description: `${aiPracticePage.name} conversation drills for spoken output.`,
    });
  }

  if (toolPage) {
    links.push({
      label: `${toolPage.name} writing practice`,
      href: `/tools/writing-practice-language-learning/${toolPage.slug}`,
      description: `Practice ${toolPage.name} prompts and corrections another way.`,
    });
    links.push({
      label: `${toolPage.name} fill-in-the-gap`,
      href: `/tools/fill-in-the-gap-language-practice/${toolPage.slug}`,
      description: `Use visual gap exercises to reinforce ${toolPage.name} patterns.`,
    });
  }

  if (page.blogSlug) {
    links.push({
      label: `Best ${page.languageName} learning apps`,
      href: `/blog/${page.blogSlug}`,
      description: `Read the broader ${page.languageName} app comparison guide.`,
    });
  }

  return links;
}

function buildSources(page: RawDuolingoAlternativePage): DuolingoAlternativeSource[] {
  const sources: DuolingoAlternativeSource[] = [
    {
      label: "Duolingo official course directory",
      href: "https://en.duolingo.com/courses",
    },
    {
      label: "Duolingo vs ChickyTutor comparison",
      href: "/compare/duolingo-vs-chickytutor",
    },
  ];

  if (page.blogSlug) {
    sources.push({
      label: `ChickyTutor ${page.languageName} app comparison`,
      href: `/blog/${page.blogSlug}`,
    });
  }

  return sources;
}

function buildTitle(languageName: string): string {
  return `Duolingo Alternatives for ${languageName} Speaking Practice | ChickyTutor`;
}

function buildDescription(languageName: string): string {
  return `Compare Duolingo alternatives for ${languageName} learners who need speaking practice, correction loops, and live AI tutor conversations.`;
}

function getLanguageCode(learnSlug: string): string {
  const targetPage = getLanguageTargetPage(learnSlug);
  if (!targetPage) {
    throw new Error(`Missing language target page for Duolingo alternative: ${learnSlug}`);
  }
  return targetPage.targetCode;
}

export const duolingoAlternativePages: DuolingoAlternativePage[] = rawPages.map((page) => ({
  ...page,
  languageCode: getLanguageCode(page.learnSlug),
  title: buildTitle(page.languageName),
  description: buildDescription(page.languageName),
  updatedAt,
  supportLinks: buildSupportLinks(page),
  sources: buildSources(page),
}));

export function getDuolingoAlternativePage(slug: string): DuolingoAlternativePage | undefined {
  return duolingoAlternativePages.find((page) => page.slug === slug);
}

export function getDuolingoAlternativePagePath(page: DuolingoAlternativePage): string {
  return `/compare/duolingo-alternatives/${page.slug}`;
}

export function getDuolingoAlternativeStaticParams(): Array<{ language: string }> {
  return duolingoAlternativePages.map((page) => ({ language: page.slug }));
}
